import type { DrilldownSortMode } from "../stores/drilldownFilters.store"
import { formatBrandName } from "@/features/dashboard/lib/format"
import type { CardStat, CompetitorCreativeCardModel } from "./drilldown-card.types"
import { resolveDupBadgeCount } from "./drilldown-card-format"
import { GCS_BASE, buildAdLibUrl, extractString, extractNumber, parseCarouselSlides } from "./drilldown-row"

const PRIMARY_DOT: CardStat["dotColor"] = "#6c4bff"
const SECONDARY_DOT: CardStat["dotColor"] = "#464b67"

/**
 * Maps a raw Competitors BQ row into the redesigned card's view model. Does NOT
 * take `sortMode` — the mapper only extracts fields; `computeCompetitorMetricPair`
 * (below) reads those fields at render time, once the card knows the active sort.
 * This avoids re-mapping every row whenever the user changes the sort dropdown.
 */
export function mapCompetitorCardRow(row: Record<string, unknown>, index = 0): CompetitorCreativeCardModel {
  const hash = extractString(row.hash)
  const adId = extractString(row.ad_id)
  const competitor = extractString(row.competitor)
  const objectType = extractString(row.object_type)
  const carouselSlides = parseCarouselSlides(row, competitor) ?? undefined
  const isCarousel = Boolean(carouselSlides)
  const ext = !isCarousel && objectType.toLowerCase() === "image" ? "png" : "mp4"
  const assets = extractNumber(row.Assets) ?? 0

  return {
    // Falls back to a row-index-derived key (never ""), same as DrilldownListView's
    // defaultRowKey — rows missing both identifiers must not collide as React/virtualizer keys.
    id: hash || adId || `row-${index}`,
    hash,
    competitor,
    brandName: formatBrandName(competitor),
    logoUrl: extractString(row.logo) || null,
    isVideo: !isCarousel && objectType.toLowerCase() !== "image",
    isCarousel,
    thumbnailUrl: hash ? `${GCS_BASE}/${competitor}/thumbnails/${hash}.png` : "",
    // Carousel items: empty assetUrl so the single-asset lightbox won't fire;
    // the carousel lightbox is activated via the slides prop instead.
    assetUrl: isCarousel ? "" : hash ? `${GCS_BASE}/${competitor}/assets/${hash}.${ext}` : "",
    carouselSlides,
    adLibUrl: buildAdLibUrl(adId),
    days: extractNumber(row.Days) ?? 0,
    assets,
    daysPerAsset: extractNumber(row.DaysPerAsset) ?? 0,
    dupCount: resolveDupBadgeCount(assets),
    minDate: extractString(row.min_date),
    maxDate: extractString(row.max_date),
    effectiveStatus: extractString(row.effective_status).toLowerCase(),
    industry: extractString(row.industry),
  }
}

/**
 * Resolves the card's primary/secondary metric pair from the active `competitorSortMode`.
 * `daysPerAsset` is read directly from a backend-computed, NULLIF-guarded, pre-rounded
 * column (services/workspace/src/bq-queries/drilldown.sql.ts:456) — never recomputed as
 * `days / assets` here. Dot colors are positional (primary always purple, secondary
 * always slate), matching the Figma spec literally rather than being metric-specific.
 * `Date` has no numeric "big" representation, so it falls back to the Days/Assets pair.
 */
export function computeCompetitorMetricPair(
  sortMode: DrilldownSortMode,
  v: { days: number; assets: number; daysPerAsset: number },
): { primary: CardStat; secondary: CardStat } {
  const days: CardStat = { label: "Days", value: String(v.days), dotColor: PRIMARY_DOT }
  const assets: CardStat = { label: "Assets", value: String(v.assets), dotColor: SECONDARY_DOT }
  const daysPerAsset: CardStat = {
    label: "Days/Assets",
    value: v.daysPerAsset.toFixed(1),
    dotColor: PRIMARY_DOT,
  }

  switch (sortMode) {
    case "Days":
      return { primary: days, secondary: { ...assets, dotColor: SECONDARY_DOT } }
    case "Assets":
      return { primary: { ...assets, dotColor: PRIMARY_DOT }, secondary: { ...days, dotColor: SECONDARY_DOT } }
    case "Days/Assets":
    case "Date":
    default:
      return { primary: daysPerAsset, secondary: { ...assets, dotColor: SECONDARY_DOT } }
  }
}
